import { useMemo, useState } from "react";
import { View, ScrollView, Text } from "react-native";
import { CheckBox } from "react-native-elements";
import AdminInput from "../AdminInput";
import AdminAlert from "../AdminAlert";
import { ADMIN_COLORS, ADMIN_FONT } from "../../config/theme/adminColors";
import { useCatalogProducts } from "../../queries/useCatalogProducts";
import { useCategories } from "../../queries/useCategories";
import {
  getCatalogContentSignature,
  getCatalogEquivalentCandidates,
  hasCatalogEquivalentMatches,
  hasSameCatalogContent,
} from "../../utils/catalogContentSignature";

const TYPE_LABEL = {
  product: "Produto",
  plan: "Plano",
  course: "Curso",
};

const LINK_SECTION_LABEL = "Vincular a categorias ou módulos";

/**
 * Seleciona OUTROS produtos/planos/cursos (turmas/lançamentos) que liberam o
 * MESMO conteúdo deste item. Grava em `equivalentIds` — ver getCatalogGroup em
 * utils/accessUtils.js, a mesma lógica usada pelo app mobile.
 */
export function CatalogEquivalentsPicker({ selfId, value, onChange }) {
  const [search, setSearch] = useState("");
  const { data: catalogProducts = [], isLoading: loadingProducts } = useCatalogProducts();
  const { data: categories = [], isLoading: loadingCategories } = useCategories();

  const selfProduct = useMemo(
    () => catalogProducts.find((item) => item.id === selfId),
    [catalogProducts, selfId]
  );

  const selectedIds = useMemo(() => (value || []).map((eq) => eq.id), [value]);

  const selfSignature = useMemo(
    () => (selfProduct ? getCatalogContentSignature(selfProduct, categories) : ""),
    [selfProduct, categories]
  );

  const hasMatches = useMemo(
    () => hasCatalogEquivalentMatches(selfProduct, catalogProducts, categories, selfId),
    [selfProduct, catalogProducts, categories, selfId]
  );

  const allCandidates = useMemo(
    () =>
      getCatalogEquivalentCandidates(selfProduct, catalogProducts, categories, {
        selfId,
        selectedIds,
      }),
    [selfProduct, catalogProducts, categories, selfId, selectedIds]
  );

  const candidates = useMemo(() => {
    const term = search.trim().toLowerCase();
    return allCandidates.filter(
      (item) => !term || (item.name || "").toLowerCase().includes(term)
    );
  }, [allCandidates, search]);

  const isSelected = (item) => selectedIds.includes(item.id);

  const isLegacyMismatch = (item) =>
    isSelected(item) && !hasSameCatalogContent(selfProduct, item, categories);

  const toggle = (item) => {
    if (!isSelected(item) && !hasSameCatalogContent(selfProduct, item, categories)) {
      return;
    }

    const current = value || [];
    const next = isSelected(item)
      ? current.filter((eq) => eq.id !== item.id)
      : [...current, { id: item.id, type: item.type }];
    onChange(next);
  };

  const isLoading = loadingProducts || loadingCategories;
  const showPicker = hasMatches || selectedIds.length > 0;

  return (
    <View>
      {isLoading && (
        <Text style={{ fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.muted }}>
          Carregando opções...
        </Text>
      )}

      {!isLoading && !selfSignature && (
        <AdminAlert variant="info">
          Este produto ainda não libera nenhum módulo. Use a seção{" "}
          <Text style={{ fontFamily: ADMIN_FONT, fontWeight: "600" }}>{LINK_SECTION_LABEL}</Text>
          {" "}deste formulário para definir o conteúdo antes de vincular equivalentes.
        </AdminAlert>
      )}

      {!isLoading && selfSignature && !showPicker && (
        <AdminAlert variant="info">
          Nenhum outro produto, plano ou curso libera exatamente as mesmas categorias e módulos.
          Se dois ou mais produtos deveriam ser equivalentes, abra cada um e confira{" "}
          <Text style={{ fontFamily: ADMIN_FONT, fontWeight: "600" }}>{LINK_SECTION_LABEL}</Text>
          {" "}— as seleções precisam coincidir em todas as categorias.
        </AdminAlert>
      )}

      {!isLoading && showPicker && (
        <>
          <AdminInput
            placeholder="Buscar produto, plano ou curso..."
            value={search}
            onChangeText={setSearch}
          />
          <ScrollView
            style={{
              maxHeight: 180,
              marginTop: 8,
              borderWidth: 1,
              borderColor: ADMIN_COLORS.border,
              borderRadius: 10,
              backgroundColor: ADMIN_COLORS.surface,
            }}
            nestedScrollEnabled
          >
            {candidates.length === 0 && (
              <Text
                style={{
                  fontFamily: ADMIN_FONT,
                  fontSize: 13,
                  color: ADMIN_COLORS.muted,
                  padding: 12,
                }}
              >
                Nenhum resultado para esta busca.
              </Text>
            )}
            {candidates.map((item) => (
              <CheckBox
                key={item.id}
                title={`${item.name || "(sem nome)"} — ${TYPE_LABEL[item.type] || item.type}${
                  isLegacyMismatch(item) ? " (conteúdo diferente — desmarque para remover)" : ""
                }`}
                checked={isSelected(item)}
                onPress={() => toggle(item)}
                containerStyle={{ backgroundColor: "transparent", paddingVertical: 4 }}
                textStyle={{
                  fontFamily: ADMIN_FONT,
                  fontSize: 13,
                  fontWeight: "400",
                  color: isLegacyMismatch(item) ? ADMIN_COLORS.warnText : ADMIN_COLORS.ink,
                }}
              />
            ))}
          </ScrollView>
        </>
      )}
    </View>
  );
}

export default CatalogEquivalentsPicker;
